/**
 * crate.module.css — the home route ledger. `docs/design-direction.md` §8.2.
 *
 * WHAT IS NOT HERE, ON PURPOSE
 *
 * The ledger's own grid — one column, no gutter, no gap — belongs to
 * `states.module.css`'s `.ledger` frame, so loading, empty, error and loaded content
 * occupy the *same* grid by construction (§8.7–§8.10). The row itself — the 44px
 * `LabelChip`, the one-line title, the meta line, the hairline that steps to
 * `--border-strong` every fourth row, the now-playing accent rule — is `TrackRow`
 * (T36), attached here verbatim. This file owns only what a *screen* legitimately
 * owns: the masthead, the filter strip, the day/drop divider rows, and the BPM column
 * this screen prints beside `TrackRow` (see `Crate.tsx`'s header for why that column
 * lives here and not inside `TrackRow`).
 *
 * THE RULES EVERY DECLARATION BELOW OBEYS
 *
 *  1. **Semantic aliases only** (§3.2). No `--ink-*`, `--paper-*`, `--stock-*`,
 *     `--warm-*` or `--cool` is reachable from this file.
 *  2. **Radii are `var(--r-cut)` (0) or `var(--r-round)` (999px)** — nothing between
 *     (§5.3). No radius is declared here at all: every edge in this file is a hard cut.
 *  3. **Separation is a 1px printed rule, never a shadow** (§5.5).
 *  4. **Every length that can be a token is a token** — `--s1 --s2 --s3 --s4 --s6
 *     --gutter --drop`. `font-size` never appears: type is set by the `.t-*` classes
 *     of `type.css`, and a local `font-size` would opt text out of §4.4's font-failure
 *     degradation.
 *  5. **Green does not exist** (§3.1, §12.1). No hue in 70–170° is reachable here.
 *  6. **Zero `mix-blend-mode`.** The blend budget is three per screen (§6.13), already
 *     spent by the grain layer, the ghost and the plate art duotone; a ledger row gets
 *     none, ever (§8.2, §9.3).
 *
 * `prefers-reduced-motion` is handled globally by `motion.css` (forces every animation
 * to a single 1ms-effective iteration), so the ledger stagger below is written so the
 * resting style is already the finished style: with motion reduced a row is simply
 * already in place.
 */

/* ────────────────────────────────────────────────────────────────────────────────
   Shared furniture.
   ──────────────────────────────────────────────────────────────────────────────── */

:global(.t-mono).cyr {
  letter-spacing: 0.02em;
  text-transform: none;
}

.screen {
  display: grid;
  grid-template-columns: 1fr;
  align-content: start;
}

/* ────────────────────────────────────────────────────────────────────────────────
   The masthead — SELECTION over a graded halftone, the true count/runtime, the
   filter strip (§8.2).
   ──────────────────────────────────────────────────────────────────────────────── */

.masthead {
  display: grid;
  gap: var(--s2);
  padding-inline: var(--gutter);
  padding-block: var(--s6) var(--s4);
}

.mastheadTitle {
  margin: 0;
  color: var(--text);
}

.mastheadMeta {
  color: var(--text-2);
}

/* 32px visual chips inside a 44px hit row (§5.6, §9.7), scrolling with a 16px mask
   fade at both ends so the strip reads as continuing rather than ending. Printed as
   static markers — see `Crate.tsx`'s header for why none of the five is a toggle. */
.filters {
  display: flex;
  gap: var(--s2);
  align-items: center;
  min-block-size: var(--drop);
  margin-block-start: var(--s1);
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-mask-image: linear-gradient(
    90deg,
    transparent,
    #000 var(--s4),
    #000 calc(100% - var(--s4)),
    transparent
  );
  mask-image: linear-gradient(
    90deg,
    transparent,
    #000 var(--s4),
    #000 calc(100% - var(--s4)),
    transparent
  );
}

.filterChip {
  flex: none;
}

/* ────────────────────────────────────────────────────────────────────────────────
   The ledger body — one column, hairline-ruled, virtualised (§8.2, §10.2).
   ──────────────────────────────────────────────────────────────────────────────── */

.ledger {
  display: block;
  margin: 0;
  padding: 0;
  list-style: none;
}

/*
 * `TrackRow` occupies the first column at its own natural (56px) height; the BPM
 * figure is a second column this screen owns, so the container query below can drop
 * it independently of `TrackRow`'s own meta line (§8.2's "the BPM column drops below
 * 360px, never squeezed").
 */
.trackCell {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: stretch;
}

.bpm {
  display: flex;
  flex: none;
  align-items: center;
  padding-inline-end: var(--gutter);
  color: var(--text-3);
  border-block-end: 1px solid var(--border);
}

/* The same bar-line metre as `TrackRow`'s own fourth-row rule, computed by the caller
   from the same track-only index so the two hairlines land on the same row. */
.bpmStrong {
  border-block-end-color: var(--border-strong);
}

/* T41 acceptance 5: the BPM column is dropped, never squeezed, below 360px of the
   ledger's own inline size — `VirtualList`'s wrapper is the `container-type:
   inline-size` context this query resolves against. */
@container (max-width: 359px) {
  .bpm {
    display: none;
  }
}

/*
 * The ledger stagger (§7.2): 18ms apart, capped at 12 rows, drawn in 2px out of
 * register — the crate-digging register lock the search results already use, at the
 * ledger's own cadence. `Crate.tsx` applies this class only while `enteredRef` has not
 * yet flipped, so it cannot replay when a row remounts from a later scroll.
 */
@keyframes bl-crate-register {
  from {
    opacity: 0;
    transform: translateX(2px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.stagger {
  animation: bl-crate-register var(--t16) var(--ease-cut) 1 both;
  animation-delay: calc(var(--i, 0) * 18ms);
}

/* ────────────────────────────────────────────────────────────────────────────────
   Sticky day headers and flyer drops — real `sections` from the catalogue response,
   never a synthetic cadence (§6.4, §8.2).
   ──────────────────────────────────────────────────────────────────────────────── */

/* 48px sticky, panel ground, a 1px `--border-strong` bottom edge — the same recipe
   as the app's own screen-contract header (§8, `states.module.css` `.header`), so a
   day boundary reads as chrome rather than as one more row. */
.sectionHeader {
  position: sticky;
  inset-block-start: 0;
  z-index: var(--z-sticky);
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-block-size: 48px;
  padding-inline: var(--gutter);
  background: var(--bg-panel);
  border-block-end: 1px solid var(--border-strong);
}

.sectionCount {
  color: var(--text-3);
}

/* The flyer's own rotation, tape and hard-cut edge are all inside `FlyerDivider`
   (§6.11, §8.2); this wrapper only keeps it a valid `<li>` with no bullet. */
.flyerRow {
  display: block;
}
